/**
 * Provides getter interface for configuration data
 */
export class ConfigurationManager {
  /**
   * Instantiate root config manager using data from global scope (`window.__APP_CONFIG__`)
   * which should be provided by server side
   *
   * @returns {ConfigurationManager}
   */
  static createFromBrowserGlobalObject() {
    const configData = window.__APP_CONFIG__;

    if (!configData) {
      throw new Error("App config data hasn't found in global scope `window.__APP_CONFIG__`");
    }

    return new ConfigurationManager(configData);
  }

  static resetBrowserGlobalObject() {
    delete window.__APP_CONFIG__;
  }

  constructor(configData) {
    Object.defineProperty(this, '__config__', {
      configurable: false,
      writable: false,
      value: configData,
    });

    Object.freeze(this.__config__);
  }

  /**
   * Parse string literals and substitute with corresponding config values
   * E.g.
   *    config data --> {test: 'World', message: 'Hello {{test}}'}
   *    'Hello {{test}}' --> 'Hello World'
   *
   * @param {String} string
   * @param {String} originConfigPath
   * @param {Array<String>} [tokensStack] - stack of resolved literal token. Specially to track and prevent circular dependencies
   * @returns {string}
   * @private
   */
  _resolveStringLiterals(string, originConfigPath, tokensStack = []) {
    return string.replace(/<<\s*([\w.]+)\s*>>/g, (match, literal) => {
      if (literal === originConfigPath) {
        throw new Error(`ConfigurationManager: Config path <<${literal}>> points to itself`);
      }

      if (tokensStack.length && tokensStack.includes(literal)) {
        throw new Error(
          `ConfigurationManager: there is a circular dependency in ${[originConfigPath, ...tokensStack].join(' --> ')}`,
        );
      }

      tokensStack.push(literal);

      return this.get(literal, false, tokensStack);
    });
  }

  /**
   * Get configuration data
   *
   * @param {String} configPath - dot notation path in root config data object
   * @param {Bool} [ignoreLiterals] - do not resolve string literals
   * @param {Array<String>} [_tokensStack] - for internal usage only
   * @returns {*}
   */
  get(configPath, ignoreLiterals = false, _tokensStack) {
    let configValue = configPath.split('.').reduce((res, pathToken) => {
      if (!res[pathToken]) {
        throw new Error(`ConfigurationManager: config "${configPath}" isn't found`);
      }

      return res[pathToken];
    }, this.__config__);

    if (typeof configValue === 'string' && !ignoreLiterals) {
      configValue = this._resolveStringLiterals(configValue, configPath, _tokensStack);
    }
    return configValue;
  }

  /**
   * Create a new instance of ConfigurationManager with nested object data
   *
   * @param {String} configPath
   * @returns {ConfigurationManager}
   */
  createChild(configPath) {
    const derivedConfigData = this.get(configPath);

    if (typeof derivedConfigData !== 'object' && derivedConfigData !== null) {
      throw new Error(
        `ConfigurationManager.childPath: supplied "${configPath}" should object data instead of "${derivedConfigData}"`,
      );
    }

    return new ConfigurationManager(derivedConfigData);
  }
}

export default ConfigurationManager;
